import {
  useGetCustomClinicalCharacteristicsMutation,
  type CustomClinicalCharacteristicsRequest as ApiRequest,
  type CustomClinicalCharacteristicsResponse as ApiResponse,
} from '@verantos-dev/data-access-cohort-summary';
import { CohortSummaryAttributeResult, DomainType } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { mapDomainToApiDomain } from '../utils/domainMappingUtils';

export interface CustomClinicalCharacteristicsRequest {
  conceptGroups: string[];
  domain: DomainType;
}

export interface CustomClinicalCharacteristicsResponse {
  data: ApiResponse;
}

// Helper function to convert concept groups to characteristics format
export const convertConceptGroupsToCharacteristics = (
  conceptGroups: ConceptGroup[]
): CohortSummaryAttributeResult[] => {
  return conceptGroups.map((group) => ({
    attributeName: group.conceptGroupName,
    attributeCount: group.conceptCount || 0,
    attributePercentage: 0, // This will be populated by the backend
  }));
};

/**
 * API service for custom clinical characteristics using real backend endpoints
 */
export class CustomClinicalCharacteristicsApiService {
  /**
   * Get custom clinical characteristics for selected concept groups
   */
  static async getCustomClinicalCharacteristics(
    projectId: string,
    cohortProfileId: string,
    request: CustomClinicalCharacteristicsRequest,
    mutationTrigger: ReturnType<typeof useGetCustomClinicalCharacteristicsMutation>[0]
  ): Promise<CustomClinicalCharacteristicsResponse> {
    const apiRequest: ApiRequest = {
      conceptGroups: request.conceptGroups,
      domain: mapDomainToApiDomain(request.domain, 'lowercase'),
    };

    const result = await mutationTrigger({
      projectId,
      cohortProfileId,
      request: apiRequest,
    }).unwrap();

    return {
      data: result.data,
    };
  }

  /**
   * Get top 10 concept groups for a domain (existing behavior)
   */
  static getTop10ConceptGroups(
    domain: DomainType,
    allData: CohortSummaryAttributeResult[]
  ): CohortSummaryAttributeResult[] {
    // Sort by percentage descending and take top 10
    return allData
      .sort((a, b) => (b.attributePercentage || 0) - (a.attributePercentage || 0))
      .slice(0, 10);
  }
}
